import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Crown, Star, Award, Trophy, Zap, Target, Medal, Flame, Shield, Gem, Sparkles } from 'lucide-react';
import { EarnedBadge } from '@/services/leaderboard.service';

interface BadgePopupProps {
  badges: EarnedBadge[];
  onClose: () => void;
}

// Map icon_type to Lucide icons
const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  crown: Crown,
  star: Star,
  award: Award,
  trophy: Trophy,
  lightning: Zap,
  zap: Zap,
  target: Target,
  medal: Medal,
  flame: Flame,
  shield: Shield,
  gem: Gem,
};

// Enhanced rarity styles with more vibrant colors
const rarityStyles: Record<string, {
  bg: string;
  headerBg: string;
  border: string;
  text: string;
  glow: string;
  iconBg: string;
  buttonBg: string;
  particles: string;
}> = {
  common: {
    bg: 'bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100',
    headerBg: 'bg-gradient-to-br from-slate-200 via-gray-100 to-slate-200',
    border: 'border-slate-300',
    text: 'text-slate-600',
    glow: '',
    iconBg: 'bg-gradient-to-br from-slate-100 to-slate-200',
    buttonBg: 'bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700',
    particles: '#94a3b8'
  },
  rare: {
    bg: 'bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100',
    headerBg: 'bg-gradient-to-br from-blue-400 via-cyan-400 to-blue-500',
    border: 'border-blue-400',
    text: 'text-blue-600',
    glow: 'shadow-[0_0_60px_rgba(59,130,246,0.5)]',
    iconBg: 'bg-gradient-to-br from-blue-100 to-cyan-100',
    buttonBg: 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600',
    particles: '#3b82f6'
  },
  epic: {
    bg: 'bg-gradient-to-br from-purple-50 via-violet-50 to-purple-100',
    headerBg: 'bg-gradient-to-br from-purple-500 via-violet-500 to-purple-600',
    border: 'border-purple-400',
    text: 'text-purple-600',
    glow: 'shadow-[0_0_80px_rgba(147,51,234,0.5)]',
    iconBg: 'bg-gradient-to-br from-purple-100 to-violet-100',
    buttonBg: 'bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600',
    particles: '#a855f7'
  },
  legendary: {
    bg: 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-100',
    headerBg: 'bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500',
    border: 'border-amber-400',
    text: 'text-amber-600',
    glow: 'shadow-[0_0_100px_rgba(245,158,11,0.6)]',
    iconBg: 'bg-gradient-to-br from-amber-100 to-yellow-100',
    buttonBg: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
    particles: '#f59e0b'
  }
};

// Confetti particle component
const Confetti = ({ color, delay }: { color: string; delay: number }) => {
  const randomX = Math.random() * 100;
  const randomRotation = Math.random() * 360;
  const randomDuration = 2 + Math.random() * 2;

  return (
    <div
      className="absolute w-2 h-2 rounded-sm opacity-0"
      style={{
        left: `${randomX}%`,
        top: '-10px',
        backgroundColor: color,
        transform: `rotate(${randomRotation}deg)`,
        animation: `confetti-fall ${randomDuration}s ease-out ${delay}s forwards`,
      }}
    />
  );
};

// Sparkle component for legendary badges
const SparkleEffect = ({ color }: { color: string }) => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${20 + Math.random() * 60}%`,
            top: `${20 + Math.random() * 60}%`,
            animation: `sparkle ${1.5 + Math.random()}s ease-in-out ${Math.random() * 2}s infinite`,
          }}
        >
          <Sparkles className="w-4 h-4" style={{ color }} />
        </div>
      ))}
    </div>
  );
};

export default function BadgePopup({ badges, onClose }: BadgePopupProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [showConfetti, setShowConfetti] = useState(true);
  const hasMultipleBadges = badges.length > 1;
  const currentBadge = badges[currentIndex];

  useEffect(() => {
    // Reset animation when badge changes
    setIsAnimating(true);
    setShowConfetti(true);
    const timer = setTimeout(() => setIsAnimating(false), 800);
    const confettiTimer = setTimeout(() => setShowConfetti(false), 4000);
    return () => {
      clearTimeout(timer);
      clearTimeout(confettiTimer);
    };
  }, [currentIndex]);

  if (!currentBadge) return null;

  const handleNext = () => {
    if (currentIndex < badges.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleContinue = () => {
    if (currentIndex < badges.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onClose();
    }
  };

  const rarity = currentBadge.rarity?.toLowerCase() || 'common';
  const styles = rarityStyles[rarity] || rarityStyles.common;
  const IconComponent = iconMap[currentBadge.icon_type?.toLowerCase()] || Award;
  const isLegendary = rarity === 'legendary';
  const isEpicOrHigher = rarity === 'epic' || rarity === 'legendary';

  // Generate confetti colors based on rarity
  const confettiColors = rarity === 'legendary'
    ? ['#f59e0b', '#fbbf24', '#f97316', '#fb923c', '#fcd34d']
    : rarity === 'epic'
    ? ['#a855f7', '#c084fc', '#8b5cf6', '#a78bfa', '#d946ef']
    : rarity === 'rare'
    ? ['#3b82f6', '#60a5fa', '#06b6d4', '#22d3ee', '#0ea5e9']
    : ['#94a3b8', '#cbd5e1', '#64748b', '#9ca3af', '#6b7280'];

  return (
    <>
      {/* CSS Animations */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            opacity: 1;
            transform: translateY(0) rotate(0deg) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(400px) rotate(720deg) scale(0.5);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1) rotate(180deg);
          }
        }

        @keyframes badge-entrance {
          0% {
            opacity: 0;
            transform: scale(0.3) rotate(-10deg);
          }
          50% {
            transform: scale(1.1) rotate(3deg);
          }
          70% {
            transform: scale(0.95) rotate(-2deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }

        @keyframes icon-pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes shine {
          0% {
            left: -100%;
          }
          100% {
            left: 100%;
          }
        }

        @keyframes glow-pulse {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.1);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        @keyframes text-reveal {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .badge-card {
          animation: badge-entrance 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .icon-container {
          animation: icon-pulse 2s ease-in-out infinite;
        }

        .floating {
          animation: float 3s ease-in-out infinite;
        }

        .text-reveal {
          animation: text-reveal 0.5s ease-out forwards;
        }

        .text-reveal-delay-1 {
          animation: text-reveal 0.5s ease-out 0.1s forwards;
          opacity: 0;
        }

        .text-reveal-delay-2 {
          animation: text-reveal 0.5s ease-out 0.2s forwards;
          opacity: 0;
        }

        .text-reveal-delay-3 {
          animation: text-reveal 0.5s ease-out 0.3s forwards;
          opacity: 0;
        }
      `}</style>

      <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4 common-modal-overlay">
        {/* Confetti */}
        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <Confetti
                key={i}
                color={confettiColors[i % confettiColors.length]}
                delay={Math.random() * 0.5}
              />
            ))}
          </div>
        )}

        <div
          className={`relative ${styles.bg} rounded-3xl shadow-2xl max-w-sm w-full max-h-[90vh] overflow-hidden flex flex-col ${styles.glow} ${isAnimating ? 'badge-card' : ''}`}
        >
          {/* Sparkles for legendary/epic */}
          {isEpicOrHigher && <SparkleEffect color={styles.particles} />}

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full text-gray-500 hover:text-gray-700 hover:bg-white transition-all z-20 shadow-sm"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Badge count indicator */}
          {hasMultipleBadges && (
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-bold text-gray-700 shadow-sm z-20">
              {currentIndex + 1} of {badges.length}
            </div>
          )}

          {/* Scrolls inside the rounded card, so the scrollbar never squares a corner. */}
          <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Header with badge icon */}
          <div className={`relative ${styles.headerBg} px-6 pt-12 pb-16 sm:pt-16 sm:pb-20 overflow-hidden`}>
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px),
                                  radial-gradient(circle at 80% 50%, white 1px, transparent 1px),
                                  radial-gradient(circle at 40% 20%, white 1px, transparent 1px),
                                  radial-gradient(circle at 60% 80%, white 1px, transparent 1px)`,
                backgroundSize: '100px 100px',
                animation: 'float 6s ease-in-out infinite'
              }} />
            </div>

            {/* Glow effect behind icon */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full blur-3xl"
              style={{
                background: `radial-gradient(circle, ${rarity === 'legendary' ? 'rgba(251,191,36,0.6)' : rarity === 'epic' ? 'rgba(168,85,247,0.5)' : rarity === 'rare' ? 'rgba(59,130,246,0.4)' : 'rgba(148,163,184,0.3)'}, transparent)`,
                animation: isEpicOrHigher ? 'glow-pulse 2s ease-in-out infinite' : undefined
              }}
            />

            {/* Badge icon container */}
            <div className="relative flex justify-center floating">
              <div className={`relative ${styles.iconBg} w-28 h-28 rounded-2xl ${styles.border} border-4 flex items-center justify-center shadow-xl icon-container overflow-hidden`}>
                {/* Shine effect */}
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                    animation: 'shine 3s ease-in-out infinite'
                  }}
                />

                {currentBadge.badge_url ? (
                  <img
                    src={currentBadge.badge_url}
                    alt={currentBadge.badge_name}
                    className="w-20 h-20 object-contain relative z-10"
                  />
                ) : (
                  <IconComponent
                    className="w-14 h-14 relative z-10"
                    style={{ color: currentBadge.icon_color || '#6b7280' }}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 sm:px-6 pt-6 pb-5 sm:pb-6 text-center -mt-8 relative z-10">
            {/* Rarity badge */}
            <div className="flex justify-center mb-4">
              <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${styles.text} bg-white shadow-md border ${styles.border}`}>
                {isLegendary && <Sparkles className="w-3 h-3" />}
                {rarity}
                {isLegendary && <Sparkles className="w-3 h-3" />}
              </span>
            </div>

            {/* Congratulations */}
            <p className="text-sm font-medium text-gray-500 mb-1 text-reveal">
              Congratulations!
            </p>

            {/* Badge name */}
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight mb-2 break-words text-reveal-delay-1">
              {currentBadge.badge_name}
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-sm mb-5 text-reveal-delay-2 leading-relaxed">
              {currentBadge.description}
            </p>

            {/* Badge stats — two equal labelled tiles. As free-width pills, a
                long category squeezed "5650 XP" onto two lines and the two
                pills ended up different heights with their icons mid-text. */}
            <div className={`grid ${currentBadge.category ? 'grid-cols-2' : 'grid-cols-1 max-w-[160px] mx-auto'} gap-2 mb-6 text-reveal-delay-3`}>
              <div className="flex flex-col items-center justify-center px-2 py-2.5 bg-amber-50 rounded-xl border border-amber-100">
                <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                  <Zap className="w-3 h-3" />
                  XP
                </span>
                <span className="text-base font-bold text-amber-700 whitespace-nowrap">
                  {(currentBadge.xp_required ?? 0).toLocaleString()}
                </span>
              </div>
              {currentBadge.category && (
                <div className="flex flex-col items-center justify-center px-2 py-2.5 bg-blue-50 rounded-xl border border-blue-100 min-w-0">
                  <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-blue-600">
                    <Target className="w-3 h-3" />
                    Category
                  </span>
                  <span
                    className="text-sm font-semibold text-blue-700 leading-snug line-clamp-2 break-words max-w-full"
                    title={currentBadge.category}
                  >
                    {currentBadge.category}
                  </span>
                </div>
              )}
            </div>

            {/* Pagination dots */}
            {hasMultipleBadges && (
              <div className="flex items-center justify-center gap-2 mb-5">
                {badges.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? `w-6 ${styles.buttonBg}`
                        : 'w-2 bg-gray-200 hover:bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-center gap-3">
              {hasMultipleBadges && currentIndex > 0 && (
                <button
                  onClick={handlePrev}
                  className="p-2.5 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}

              <button
                onClick={handleContinue}
                className={`flex-1 max-w-[240px] px-6 py-3 ${styles.buttonBg} text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5`}
              >
                {currentIndex < badges.length - 1 ? 'Next Badge' : 'Awesome!'}
              </button>

              {hasMultipleBadges && currentIndex < badges.length - 1 && (
                <button
                  onClick={handleNext}
                  className="p-2.5 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );
}
